import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useAppDispatch, useAppSelector } from '../../hooks/redux';
import {
  addCostComponent,
  deleteCostComponent,
  fetchCostBreakdown,
  updateAllocationMethod,
  updateCostComponent,
  updateCosts,
} from '../../store/slices/shipmentTrackerSlice';
import { addNotification } from '../../store/slices/notificationSlice';
import { useShipmentTrackerPermissions } from '../../hooks/useShipmentTrackerPermissions';
import type { ShipmentCostBreakdownRowView } from '../../api/generated/models/ShipmentCostBreakdownRowView';
import type { ShipmentCostsUpdateRequest } from '../../api/generated/models/ShipmentCostsUpdateRequest';
import { ShipmentCostComponentRequest } from '../../api/generated/models/ShipmentCostComponentRequest';
import Modal from '../ui/Modal';
import ShipmentPerProductTable from './ShipmentPerProductTable';
import { ReactComponent as DeleteIcon } from '../../assets/icons/delete.svg';

const CURRENCY_SYMBOL: Record<string, string> = { EUR: '€', USD: '$' };

const ALLOCATION_METHODS = ['value', 'volume', 'weight'] as const;

/**
 * Fixed rows map onto the flat columns of PATCH /costs. Product cost has no planned field —
 * it is derived from the items — so only its actual is editable.
 */
const FIXED_FIELDS: Record<
  string,
  {
    planned?: keyof ShipmentCostsUpdateRequest;
    actual: keyof ShipmentCostsUpdateRequest;
  }
> = {
  product: { actual: 'actualProduct' },
  transport: { planned: 'plannedTransport', actual: 'actualTransport' },
  duty: { planned: 'plannedDuty', actual: 'actualDuty' },
  clearance: { planned: 'plannedClearance', actual: 'actualClearance' },
  energy_tax: { planned: 'plannedEnergyTax', actual: 'actualEnergyTax' },
};

type Amounts = { planned: string; actual: string };
type CustomDraft = Amounts & { label: string; basis: string };

/** What the modal edits: a custom row's definition. Amounts stay inline in the table. */
type RowDefinition = {
  componentId: string | null;
  label: string;
  basis: string;
};

const toNumberOrNull = (raw: string): number | null =>
  raw.trim() !== '' ? parseFloat(raw) : null;

const isNonZero = (raw?: string | null) =>
  raw != null && raw !== '' && parseFloat(raw) !== 0;

interface Props {
  shipmentId: string;
}

const CostBreakdownTab: React.FC<Props> = ({ shipmentId }) => {
  const { t } = useTranslation();
  const dispatch = useAppDispatch();
  const { canWrite } = useShipmentTrackerPermissions();
  const { data, loading, saving, error } = useAppSelector(
    (state) => state.shipmentTracker.costBreakdown,
  );

  const [fixed, setFixed] = useState<Record<string, Amounts>>({});
  const [custom, setCustom] = useState<Record<string, CustomDraft>>({});
  const [dirty, setDirty] = useState(false);
  const [rowModal, setRowModal] = useState<RowDefinition | null>(null);
  const [pendingDelete, setPendingDelete] =
    useState<ShipmentCostBreakdownRowView | null>(null);
  const [bannerDismissed, setBannerDismissed] = useState(false);

  useEffect(() => {
    dispatch(fetchCostBreakdown(shipmentId));
  }, [dispatch, shipmentId]);

  // Re-seed the inputs from every response (including the ones mutations return), so the
  // form always reflects what the server actually stored.
  useEffect(() => {
    if (!data) return;
    const nextFixed: Record<string, Amounts> = {};
    const nextCustom: Record<string, CustomDraft> = {};
    (data.rows ?? []).forEach((row) => {
      if (row.key === 'total') return;
      if (row.isCustom && row.componentId) {
        nextCustom[row.componentId] = {
          label: row.label ?? '',
          planned: row.planned ?? '',
          actual: row.actual ?? '',
          // Seed from the raw override, never from `basis` (which is the resolved value):
          // otherwise a row that merely inherits would look pinned, and saving it would pin it.
          basis: row.basisOverride ?? '',
        };
      } else {
        nextFixed[row.key] = {
          planned: row.planned ?? '',
          actual: row.actual ?? '',
        };
      }
    });
    setFixed(nextFixed);
    setCustom(nextCustom);
    setDirty(false);
  }, [data]);

  const componentLabel = useCallback(
    (row: ShipmentCostBreakdownRowView): string => {
      if (row.isCustom) return row.label ?? '';
      const map: Record<string, string> = {
        product: t('cost_product'),
        transport: t('cost_transport'),
        duty: t('cost_duty'),
        clearance: t('cost_clearance'),
        energy_tax: t('cost_energy_tax'),
        total: t('cost_total'),
      };
      return map[row.key] ?? row.key;
    },
    [t],
  );

  const methodLabel = useCallback(
    (method: string): string => {
      const map: Record<string, string> = {
        value: t('allocation_method_value'),
        volume: t('allocation_method_volume'),
        weight: t('allocation_method_weight'),
      };
      return map[method] ?? method;
    },
    [t],
  );

  const rows = useMemo(() => data?.rows ?? [], [data]);
  const componentRows = useMemo(
    () => rows.filter((r) => r.key !== 'total'),
    [rows],
  );
  const totalRow = useMemo(() => rows.find((r) => r.key === 'total'), [rows]);

  const setFixedAmount = (key: string, field: keyof Amounts, value: string) => {
    setFixed((prev) => ({ ...prev, [key]: { ...prev[key], [field]: value } }));
    setDirty(true);
  };

  const setCustomField = (
    id: string,
    field: keyof CustomDraft,
    value: string,
  ) => {
    setCustom((prev) => ({ ...prev, [id]: { ...prev[id], [field]: value } }));
    setDirty(true);
  };

  const handleMethodChange = async (method: string) => {
    try {
      await dispatch(
        updateAllocationMethod({ id: shipmentId, allocationMethod: method }),
      ).unwrap();
    } catch {
      // notification fired in the thunk
    }
  };

  /**
   * One Save for the whole table: the fixed components go in a single PATCH, then each custom
   * row that actually changed is sent on its own (the API has no bulk endpoint for them).
   */
  const handleSave = async () => {
    const costs: ShipmentCostsUpdateRequest = {};
    Object.entries(FIXED_FIELDS).forEach(([key, fields]) => {
      const amounts = fixed[key] ?? { planned: '', actual: '' };
      if (fields.planned) {
        costs[fields.planned] = toNumberOrNull(amounts.planned) as never;
      }
      costs[fields.actual] = toNumberOrNull(amounts.actual) as never;
    });

    const changedCustom = componentRows.filter((row) => {
      if (!row.isCustom || !row.componentId) return false;
      const draft = custom[row.componentId];
      if (!draft) return false;
      return (
        draft.label !== (row.label ?? '') ||
        draft.planned !== (row.planned ?? '') ||
        draft.actual !== (row.actual ?? '') ||
        draft.basis !== (row.basisOverride ?? '')
      );
    });

    try {
      await dispatch(updateCosts({ id: shipmentId, data: costs })).unwrap();
      for (const row of changedCustom) {
        const draft = custom[row.componentId as string];
        await dispatch(
          updateCostComponent({
            id: shipmentId,
            componentId: row.componentId as string,
            data: {
              label: draft.label,
              planned: toNumberOrNull(draft.planned),
              actual: toNumberOrNull(draft.actual),
              allocationBasisOverride: draft.basis
                ? (draft.basis as ShipmentCostComponentRequest.allocationBasisOverride)
                : null,
            },
          }),
        ).unwrap();
      }
      dispatch(
        addNotification({
          type: 'success',
          title: t('success'),
          message: t('cost_saved'),
        }),
      );
      setDirty(false);
    } catch {
      // notification fired in the thunk
    }
  };

  /**
   * Saves the row definition from the modal. On edit the amounts are carried over from the
   * inline draft — the update endpoint replaces the whole row, so omitting them would wipe
   * whatever is currently in the Planned/Actual cells.
   */
  const handleRowModalSave = async () => {
    if (!rowModal || !rowModal.label.trim()) return;

    const allocationBasisOverride = rowModal.basis
      ? (rowModal.basis as ShipmentCostComponentRequest.allocationBasisOverride)
      : null;

    try {
      if (rowModal.componentId === null) {
        await dispatch(
          addCostComponent({
            id: shipmentId,
            data: {
              label: rowModal.label.trim(),
              planned: null,
              actual: null,
              allocationBasisOverride,
            },
          }),
        ).unwrap();
      } else {
        const amounts = custom[rowModal.componentId] ?? {
          planned: '',
          actual: '',
        };
        await dispatch(
          updateCostComponent({
            id: shipmentId,
            componentId: rowModal.componentId,
            data: {
              label: rowModal.label.trim(),
              planned: toNumberOrNull(amounts.planned),
              actual: toNumberOrNull(amounts.actual),
              allocationBasisOverride,
            },
          }),
        ).unwrap();
      }
      setRowModal(null);
    } catch {
      // notification fired in the thunk
    }
  };

  const handleDeleteRow = async (row: ShipmentCostBreakdownRowView) => {
    // Deleting a row that carries amounts needs confirmation (FR-4.2).
    if (isNonZero(row.planned) || isNonZero(row.actual)) {
      setPendingDelete(row);
      return;
    }
    await confirmDelete(row);
  };

  const confirmDelete = async (row: ShipmentCostBreakdownRowView) => {
    try {
      await dispatch(
        deleteCostComponent({
          id: shipmentId,
          componentId: row.componentId as string,
        }),
      ).unwrap();
    } catch {
      // notification fired in the thunk
    } finally {
      setPendingDelete(null);
    }
  };

  if (loading) return <div className="page-loading">{t('loading')}</div>;
  if (error) return <div className="page-error">{error}</div>;
  if (!data) return null;

  const sym = CURRENCY_SYMBOL[data.currency] ?? data.currency;
  const options = data.allocationMethods ?? [];

  return (
    <div className="cost-breakdown-tab">
      {/* ── Fallback banner (FR-1.6) ── */}
      {data.allocationFallbackApplied && !bannerDismissed && (
        <div className="cost-alert cost-alert--warning">
          <span>
            {t('allocation_fallback_banner', {
              requested: methodLabel(data.requestedAllocationMethod),
              applied: methodLabel(data.allocationMethod),
            })}
          </span>
          <button
            className="cost-alert__dismiss"
            onClick={() => setBannerDismissed(true)}
          >
            ×
          </button>
        </div>
      )}

      {/* ── Headline cards, on effective amounts (FR-6) ── */}
      <div className="cost-summary-cards">
        <div className="cost-summary-card">
          <div className="cost-summary-card__label">
            {t('cost_total_landed')}
          </div>
          <div className="cost-summary-card__value">
            {sym}
            {data.totalEffective ?? '-'}
          </div>
        </div>
        <div className="cost-summary-card">
          <div className="cost-summary-card__label">{t('cost_per_unit')}</div>
          <div className="cost-summary-card__value">
            {data.totalPerUnitEffective != null
              ? `${sym}${data.totalPerUnitEffective}`
              : '-'}
          </div>
        </div>
        <div className="cost-summary-card">
          <div className="cost-summary-card__label">{t('cost_per_carton')}</div>
          <div className="cost-summary-card__value">
            {data.totalPerCartonEffective != null
              ? `${sym}${data.totalPerCartonEffective}`
              : '-'}
          </div>
        </div>
        <div className="cost-summary-card cost-summary-card--status">
          <div className="cost-summary-card__label">{t('cost_status')}</div>
          <span className={`cost-status-pill cost-status-pill--${data.status}`}>
            {t(`cost_status_${data.status}`)}
          </span>
        </div>
      </div>

      {/* ── Consolidated component table (FR-4, FR-5) ── */}
      <div className="detail-card">
        <div className="cost-breakdown-table-scroll">
          <table className="cost-breakdown-table">
            <thead>
              <tr>
                <th className="cost-breakdown-table__component">
                  {t('cost_component')}
                </th>
                <th>
                  {t('cost_planned')} ({sym})
                </th>
                <th>
                  {t('cost_actual')} ({sym})
                </th>
                <th>
                  {t('cost_variance')} ({sym})
                </th>
                <th>
                  {t('cost_per_unit_col')} ({sym})
                </th>
                <th>
                  {t('cost_per_carton_col')} ({sym})
                </th>
                <th>{t('cost_pct_of_landed')}</th>
                {canWrite && <th />}
              </tr>
            </thead>
            <tbody>
              {componentRows.map((row) => {
                const id = row.componentId ?? '';
                const draft = row.isCustom ? custom[id] : undefined;
                const amounts = row.isCustom
                  ? {
                      planned: draft?.planned ?? '',
                      actual: draft?.actual ?? '',
                    }
                  : (fixed[row.key] ?? { planned: '', actual: '' });
                const fields = FIXED_FIELDS[row.key];
                const plannedEditable =
                  canWrite && (row.isCustom || !!fields?.planned);
                return (
                  <tr key={row.key}>
                    <td className="cost-breakdown-table__component">
                      {componentLabel(row)}
                      {/* Product cost and customs duty are both pinned to value (FR-3 / FR-4.4). */}
                      {row.basisLocked && (
                        <div className="cost-basis-note">
                          {t('cost_basis_locked_subtitle')}
                        </div>
                      )}
                      {/* Only worth stating when the row does NOT follow the container —
                          a null override is the default and needs no note. */}
                      {row.isCustom && row.basisOverride && (
                        <div className="cost-basis-note">
                          {methodLabel(row.basisOverride)}
                        </div>
                      )}
                      {row.basisFellBack && (
                        <div className="cost-basis-note cost-basis-warning">
                          ⚠ {t('cost_basis_fell_back')}
                        </div>
                      )}
                    </td>
                    <td>
                      {plannedEditable ? (
                        <input
                          className="input input--inline"
                          type="number"
                          min="0"
                          step="0.01"
                          placeholder="0.00"
                          value={amounts.planned}
                          onChange={(e) =>
                            row.isCustom
                              ? setCustomField(id, 'planned', e.target.value)
                              : setFixedAmount(
                                  row.key,
                                  'planned',
                                  e.target.value,
                                )
                          }
                        />
                      ) : (
                        (row.planned ?? '-')
                      )}
                    </td>
                    <td>
                      {canWrite ? (
                        // No min: actuals may be negative (credits / refunds).
                        <input
                          className="input input--inline"
                          type="number"
                          step="0.01"
                          placeholder="0.00"
                          value={amounts.actual}
                          onChange={(e) =>
                            row.isCustom
                              ? setCustomField(id, 'actual', e.target.value)
                              : setFixedAmount(
                                  row.key,
                                  'actual',
                                  e.target.value,
                                )
                          }
                        />
                      ) : (
                        (row.actual ?? '-')
                      )}
                    </td>
                    <td
                      className={
                        row.variance?.startsWith('-') ? 'text-danger' : ''
                      }
                    >
                      {row.variance ?? '-'}
                      {row.variancePct != null ? ` (${row.variancePct}%)` : ''}
                    </td>
                    <td>{row.plannedPerUnit ?? '-'}</td>
                    <td>{row.plannedPerCarton ?? '-'}</td>
                    <td>
                      {row.pctOfLanded != null ? `${row.pctOfLanded}%` : '-'}
                    </td>
                    {canWrite && (
                      <td className="cost-breakdown-table__actions-cell">
                        {row.isCustom && (
                          <>
                            <button
                              className="btn-link"
                              onClick={() =>
                                setRowModal({
                                  componentId: id,
                                  label: draft?.label ?? row.label ?? '',
                                  basis:
                                    draft?.basis ?? row.basisOverride ?? '',
                                })
                              }
                              disabled={saving}
                            >
                              {t('edit')}
                            </button>
                            <button
                              className="btn-link btn-link--danger cost-breakdown-table__delete"
                              title={t('delete')}
                              aria-label={t('delete')}
                              onClick={() => handleDeleteRow(row)}
                              disabled={saving}
                            >
                              <DeleteIcon />
                            </button>
                          </>
                        )}
                      </td>
                    )}
                  </tr>
                );
              })}
            </tbody>
            {totalRow && (
              <tfoot>
                <tr className="cost-breakdown-table__total">
                  <td className="cost-breakdown-table__component">
                    <strong>{t('cost_total')}</strong>
                  </td>
                  <td>
                    <strong>{totalRow.planned ?? '-'}</strong>
                  </td>
                  <td>
                    <strong>{totalRow.actual ?? '-'}</strong>
                  </td>
                  <td
                    className={
                      totalRow.variance?.startsWith('-') ? 'text-danger' : ''
                    }
                  >
                    <strong>{totalRow.variance ?? '-'}</strong>
                  </td>
                  <td>
                    <strong>{totalRow.plannedPerUnit ?? '-'}</strong>
                  </td>
                  <td>
                    <strong>{totalRow.plannedPerCarton ?? '-'}</strong>
                  </td>
                  <td>
                    <strong>
                      {totalRow.pctOfLanded != null
                        ? `${totalRow.pctOfLanded}%`
                        : '-'}
                    </strong>
                  </td>
                  {canWrite && <td />}
                </tr>
              </tfoot>
            )}
          </table>
        </div>

        {canWrite && (
          <div className="cost-breakdown-tab__actions">
            <button
              className="btn btn-secondary"
              onClick={() =>
                setRowModal({ componentId: null, label: '', basis: '' })
              }
              disabled={saving}
            >
              {t('cost_add_custom_row')}
            </button>
            {dirty && (
              <button
                className="btn btn-primary"
                onClick={handleSave}
                disabled={saving}
              >
                {saving ? t('saving') : t('cost_save_changes')}
              </button>
            )}
          </div>
        )}
      </div>

      {/* ── Per-product landed cost (FR-6) ── */}
      <div className="detail-card per-product-card">
        <div className="per-product-header">
          <h3>{t('per_product_title')}</h3>
        </div>
        {/* The method belongs here: this is the only table its choice actually changes. */}
        <div className="allocation-method">
          <label
            className="allocation-method__label"
            htmlFor="allocation-method-select"
          >
            {t('allocation_method')}
          </label>
          <select
            id="allocation-method-select"
            className="input allocation-method__select"
            value={data.allocationMethod}
            disabled={!canWrite || saving}
            onChange={(e) => handleMethodChange(e.target.value)}
          >
            {ALLOCATION_METHODS.map((method) => {
              const option = options.find((o) => o.method === method);
              const available = option?.available ?? true;
              const missing = option?.missing ?? [];
              const title = available
                ? undefined
                : t('allocation_method_unavailable', {
                    products: missing
                      .map((m) =>
                        [m.sku, m.productName].filter(Boolean).join(' '),
                      )
                      .join(', '),
                  });
              return (
                <option
                  key={method}
                  value={method}
                  disabled={!available}
                  title={title}
                >
                  {methodLabel(method)}
                  {available
                    ? ''
                    : ` — ${t('allocation_method_unavailable_short')}`}
                </option>
              );
            })}
          </select>
          <span className="allocation-method__hint">
            {t('allocation_method_hint')}
          </span>
          {/*
            FR-1.4 asks for a tooltip on the disabled options, but browsers do not reliably render
            `title` on <option>, so the same information is spelled out here where it is always
            visible.
          */}
          {(data.products ?? []).length === 0 ? (
            // With no products there is nothing to name as missing, so the per-method lines would
            // read "missing data for:" followed by nothing. State the actual reason instead.
            <div className="allocation-method__missing">
              {t('allocation_method_no_products')}
            </div>
          ) : (
            options
              .filter((option) => !option.available)
              .map((option) => (
                <div key={option.method} className="allocation-method__missing">
                  {t('allocation_method_unavailable_detail', {
                    method: methodLabel(option.method),
                    products: (option.missing ?? [])
                      .map((m) =>
                        [m.sku, m.productName].filter(Boolean).join(' '),
                      )
                      .join(', '),
                  })}
                </div>
              ))
          )}
        </div>
        <ShipmentPerProductTable
          componentRows={componentRows}
          products={data.products ?? []}
          totals={data.productTotals}
          componentLabel={componentLabel}
          allocationMethod={data.allocationMethod}
          methodLabel={methodLabel}
          sym={sym}
        />
      </div>

      {/* A custom row's definition (label + basis) is edited here rather than inline, so the
          table stays a grid of amounts. Planned/Actual remain inline for every row (FR-5.1). */}
      {rowModal && (
        <Modal
          isOpen
          className="cost-breakdown-row-modal"
          onClose={() => setRowModal(null)}
          title={
            rowModal.componentId === null
              ? t('cost_add_custom_row')
              : t('cost_row_modal_title_edit')
          }
          isCentered
        >
          <label className="form-label">
            {t('cost_row_label')}
            <input
              className="input"
              type="text"
              maxLength={60}
              autoFocus
              placeholder={t('cost_custom_label_placeholder')}
              value={rowModal.label}
              onChange={(e) =>
                setRowModal({ ...rowModal, label: e.target.value })
              }
            />
          </label>
          <label className="form-label">
            {t('cost_basis')}
            <select
              className="input"
              value={rowModal.basis}
              onChange={(e) =>
                setRowModal({ ...rowModal, basis: e.target.value })
              }
            >
              <option value="">
                {t('cost_basis_follow_container', {
                  method: methodLabel(data.allocationMethod),
                })}
              </option>
              {ALLOCATION_METHODS.map((method) => (
                <option
                  key={method}
                  value={method}
                  disabled={
                    !(
                      options.find((o) => o.method === method)?.available ??
                      true
                    )
                  }
                >
                  {methodLabel(method)}
                </option>
              ))}
            </select>
          </label>
          <div className="modal-buttons">
            <button
              className="btn btn-secondary"
              onClick={() => setRowModal(null)}
              disabled={saving}
            >
              {t('cancel')}
            </button>
            <button
              className="btn btn-primary"
              onClick={handleRowModalSave}
              disabled={saving || !rowModal.label.trim()}
            >
              {rowModal.componentId === null
                ? t('cost_add_row_confirm')
                : t('cost_row_save')}
            </button>
          </div>
        </Modal>
      )}

      {pendingDelete && (
        <Modal
          isOpen
          onClose={() => setPendingDelete(null)}
          title={t('cost_delete_row_title')}
          hideCloseButton
          isCentered
        >
          <p>
            {t('cost_delete_row_body', { label: pendingDelete.label ?? '' })}
          </p>
          <div className="modal-buttons">
            <button
              className="modal-button cancel-delete"
              onClick={() => setPendingDelete(null)}
              disabled={saving}
            >
              {t('cancel')}
            </button>
            <button
              className="modal-button confirm-delete"
              onClick={() => confirmDelete(pendingDelete)}
              disabled={saving}
            >
              {t('delete')}
            </button>
          </div>
        </Modal>
      )}
    </div>
  );
};

export default CostBreakdownTab;
